import { t } from "../lib/i18n";
import { dirAttrs } from "../lib/persian";
import { playCard } from "../lib/pronounce";
import { loadCardNote, saveCardNote } from "../lib/store";
import { h } from "../ui/dom";
import type { Card, Collection, CollectionColor } from "../lib/types";

/**
 * Shared row/card widgets.
 *
 * Every piece of user-authored text goes through `props.text` (i.e. textContent)
 * or `bidiText` — never innerHTML. Shared decks make card content untrusted.
 */

/**
 * Render a string with the right direction for its script.
 *
 * Direction is set on the element wrapping the text, never inherited from the
 * page: this app is inherently mixed EN/FA, and a page-level `dir` renders
 * English punctuation at the wrong end inside an RTL layout.
 */
export function bidiText(text: string, className = ""): HTMLElement {
  const { dir, lang } = dirAttrs(text);
  return h("span", {
    class: [className, lang === "fa" ? "fa" : ""].filter(Boolean).join(" "),
    dir,
    lang,
    text,
  });
}

/**
 * Stand-in for the due/new pills while they load, appended into `.card-stats`
 * alongside the card count that's already there.
 *
 * `dueSummary` reuses `loadStudySet`, so it's several round trips and resolves
 * well after the card itself renders. Rendering nothing until then made the
 * pills pop in later, shoving the footer around under the reader's thumb.
 *
 * Two pills because due + new is the common steady state. It can't be exact —
 * a collection with nothing due resolves to one pill or none — so `.counts`
 * below still carries its own min-block-size to absorb THAT difference.
 */
export function statsPlaceholder(): HTMLElement[] {
  const skel = (extra: string) =>
    h("span", { class: `skel ${extra}`, attrs: { "aria-hidden": "true" } });
  return [skel("skel-pill"), skel("skel-pill")];
}

/**
 * Stand-in for the footer bar's "Started {date}" text and its Study/Add
 * button while `dueSummary` loads. Shapes rather than a spinner — one spinner
 * per card would be busier than the content it stands in for.
 *
 * Used as-is by `collection.ts`'s own standalone study bar (a bare `.counts`
 * div, not part of a card). `collectionCard`'s footer instead uses
 * `startedPlaceholder`/`actionPlaceholder` below — split so a static delete
 * button can sit in `.counts-end` beside the async-filled action without
 * being wiped out each time that action re-renders.
 */
export function countsPlaceholder(opts: { full?: boolean } = {}): HTMLElement[] {
  const skel = (extra: string) =>
    h("span", { class: `skel ${extra}`, attrs: { "aria-hidden": "true" } });
  return [skel("skel-meta-sm"), skel(opts.full ? "skel-btn skel-btn-full" : "skel-btn")];
}

/** Stand-in for `.counts-start`'s "Started {date}" text. */
export function startedPlaceholder(): HTMLElement {
  return h("span", { class: "skel skel-meta-sm", attrs: { "aria-hidden": "true" } });
}

/** Stand-in for `.counts-action`'s Study/Add button. */
export function actionPlaceholder(): HTMLElement {
  return h("span", { class: "skel skel-btn", attrs: { "aria-hidden": "true" } });
}

/**
 * A whole card's worth of placeholder, for the wait before we know what rows
 * there are at all.
 *
 * Mirrors `collectionCard`'s shape (avatar, title block, description line,
 * progress, counts) so the reserved height in the home-page shape-memory
 * system (see home.ts) is close to what actually lands — it doesn't need to be
 * exact, since `.counts`/`.card-progress` carry their own floors and the whole
 * block gets re-measured after real content arrives.
 */
export function rowPlaceholder(): HTMLElement {
  return h("div", { class: "card", attrs: { "aria-hidden": "true" } },
    h("div", { class: "card-body" },
      h("div", { class: "card-top" },
        h("div", { class: "card-info" },
          h("div", { class: "row-main" },
            h("span", { class: "skel skel-title" }),
            h("span", { class: "skel skel-meta" }),
          ),
          h("div", { class: "card-stats" }, ...statsPlaceholder()),
        ),
        h("span", { class: "skel skel-avatar" }),
      ),
      h("div", { class: "card-progress" }, progressPlaceholder()),
    ),
    h("div", { class: "counts" },
      startedPlaceholder(),
      h("div", { class: "counts-end" }, actionPlaceholder()),
    ),
  );
}

/**
 * The eight colors a collection's icon can be tinted with.
 *
 * A fixed palette rather than a free hex value: each key is rendered via
 * `color-mix()` against the surface (see `.card-avatar` in app.css), the same
 * idiom already used for `.pill.due`/`.pill.fresh` — that's what guarantees
 * every color reads legibly in both the dark and light theme without
 * per-collection contrast checking. Must stay in sync with the CHECK
 * constraint on `collections.color` in schema.sql.
 */
export const COLLECTION_COLORS: readonly CollectionColor[] =
  ["blue", "green", "purple", "orange", "pink", "teal", "red", "yellow"];

/**
 * A curated set of emoji for the icon picker. Deliberately not "any emoji" —
 * an open-ended picker is a much bigger UI (search, categories, skin-tone
 * variants) for a feature that only needs to say "here's roughly what this
 * collection is about".
 */
export const COLLECTION_EMOJI: readonly string[] =
  ["📚", "🎬", "📺", "🗣️", "✈️", "🎯", "📝", "🎧", "⭐", "🔥", "🌍", "🎓",
   "📖", "💬", "🧠", "🎭", "🎵", "🍿", "⚡", "🌱", "🏆", "🔑", "🧩", "🗝️"];

/**
 * The card icon: the chosen emoji, or the title's initial letter when none is
 * set, tinted by the chosen color (or a neutral tint when that's unset too).
 *
 * Purely decorative — `aria-hidden`, because the title right next to it
 * already carries the same information accessibly, and a lone letter or emoji
 * read aloud on its own would just be noise.
 *
 * Shared between `collectionCard` and the add/edit form's live preview, so
 * the picker shows exactly what the card will look like.
 */
export function collectionAvatar(collection: Pick<Collection, "title" | "icon" | "color">): HTMLElement {
  const colorClass = collection.color ? `avatar-${collection.color}` : "avatar-neutral";
  const glyph = collection.icon || collection.title.trim().charAt(0).toUpperCase() || "?";
  return h("div", { class: `card-avatar ${colorClass}`, attrs: { "aria-hidden": "true" }, text: glyph });
}

/**
 * Stand-in for the "% started" progress bar while it loads — same reasoning
 * as `countsPlaceholder`, and filled in by the same `dueSummary` round trip.
 * Returns the skeleton content only, not the `.card-progress` wrapper, so
 * callers can drop it straight into an already-existing slot.
 */
export function progressPlaceholder(): HTMLElement {
  return h("span", { class: "skel skel-bar", attrs: { "aria-hidden": "true" } });
}

export function collectionCard(
  collection: Collection,
  opts: {
    onOpen?: () => void;
    action?: HTMLElement;
    /** Reserve the progress row, to be filled in async once `dueSummary` loads. */
    showProgress?: boolean;
    /** Reserve the footer bar even without an `action` in hand yet — for the
     *  same reason: `attachCounts`/`attachStudyBar` fill it in after an async
     *  `dueSummary` call. Implied by passing `action` directly (Browse's Add/
     *  Added, known synchronously). Decks and Browse's plain "mine" list pass
     *  neither, so no empty footer/divider shows where nothing will ever land. */
    showFooter?: boolean;
    /** True when the signed-in viewer owns this collection. Caller-supplied,
     *  not derived here — telling "mine" from "someone else's" needs the
     *  viewer's own id, which this function has no independent access to
     *  (Browse's "My collections" list already knows; Home would need to
     *  compare `collection.owner_id` against the session itself). */
    owned?: boolean;
  } = {},
): HTMLElement {
  const meta: string[] = [];
  if (collection.card_count) meta.push(t("cardCount", { n: collection.card_count }));
  const tintClass = collection.color ? `tint-${collection.color}` : "";

  // Official beats "mine" when somehow both are true (shouldn't happen in
  // practice — curated content has no owner — but official is the more
  // informative fact if it ever did).
  const tag = collection.is_official
    ? h("span", { class: "pill tag", text: t("official") })
    : opts.owned
      ? h("span", { class: "pill tag", text: t("mine") })
      : null;

  return h(
    "div",
    {
      class: "card tappable",
      attrs: { role: "button", tabindex: "0" },
      on: {
        click: () => opts.onOpen?.(),
        keydown: (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            opts.onOpen?.();
          }
        },
      },
    },
    h("div", { class: `card-body ${tintClass}`.trim() },
      // Two columns: tag/title/description/numbers stacked on the left,
      // the icon stretching to match their combined height on the right —
      // not a small circle beside the title, a tall image-shaped placeholder
      // alongside the whole text block.
      h("div", { class: "card-top" },
        h("div", { class: "card-info" },
          tag,
          h("div", { class: "row-main" },
            bidiText(collection.title, "row-title"),
            collection.title_fa ? bidiText(collection.title_fa, "row-sub muted") : null,
          ),
          collection.description ? bidiText(collection.description, "card-desc muted small") : null,
          // Card count up front; due/new pills join it here once `dueSummary`
          // resolves, so every number about the deck reads as one line. They
          // land in `.stats-dynamic` (styled `display: contents`) rather than
          // directly in `.card-stats`, so filling it in can't clobber the
          // static count text beside it — the two are independently owned but
          // visually one row.
          h("div", { class: "card-stats muted small" },
            meta.length ? h("span", { text: meta.join(" · ") }) : null,
            h("span", { class: "stats-dynamic" }),
          ),
        ),
        collectionAvatar(collection),
      ),
      // Its own full-width row below BOTH columns, not just the text one.
      opts.showProgress ? h("div", { class: "card-progress" }) : null,
    ),
    opts.action || opts.showFooter
      ? h("div", { class: "counts" },
          h("span", { class: "counts-start muted small" }),
          // `.counts-action` is a sub-slot, not the action itself directly:
          // `attachCounts`/`attachStudyBar` replace only its content, which
          // matters once anything else ever needs to sit in `.counts`
          // alongside it without being wiped out on every async refresh.
          h("span", { class: "counts-action" }, opts.action ?? null),
        )
      : null,
  );
}

export function cardRow(
  card: Card,
  opts: { onEdit?: () => void; onDelete?: () => void; onOpen?: () => void } = {},
): HTMLElement {
  return h(
    "div",
    {
      class: opts.onOpen ? "row tappable" : "row",
      attrs: opts.onOpen ? { role: "button", tabindex: "0" } : {},
      on: opts.onOpen
        ? {
            click: () => opts.onOpen?.(),
            keydown: (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                opts.onOpen?.();
              }
            },
          }
        : {},
    },
    h("div", { class: "row-main" },
      bidiText(card.front, "row-title"),
      bidiText(card.back, "row-sub"),
      card.ipa ? h("div", { class: "row-meta muted small", text: card.ipa }) : null,
    ),
    opts.onEdit
      ? h("button", {
          class: "ghost small",
          text: t("edit"),
          on: {
            click: (event) => {
              event.stopPropagation(); // the row itself opens the preview
              opts.onEdit?.();
            },
          },
        })
      : null,
    opts.onDelete
      ? h("button", {
          class: "ghost small icon-btn",
          text: "🗑",
          title: t("delete"),
          attrs: { type: "button", "aria-label": t("delete") },
          on: {
            click: (event) => {
              event.stopPropagation(); // the row itself opens the preview
              opts.onDelete?.();
            },
          },
        })
      : null,
  );
}

/**
 * Read-only view of a card, already revealed.
 *
 * Deliberately carries NO grading buttons: browsing a deck is not studying it, and
 * letting someone grade here would write review history for a card they were only
 * looking up — polluting the scheduler with recalls that never happened.
 */
export function openCardPreview(card: Card): void {
  const close = () => {
    overlay.remove();
    removeEventListener("keydown", onKey);
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === "Escape") close();
  };

  const panel = h("div", {
    class: "preview-panel",
    attrs: { role: "dialog", "aria-modal": "true", "aria-label": t("cardPreview") },
    // Stop clicks inside the panel reaching the backdrop's close handler.
    on: { click: (event) => event.stopPropagation() },
  },
    h("div", { class: "preview-head" },
      h("span", { class: "muted small", text: t("cardPreview") }),
      h("button", { class: "ghost small", text: "✕", title: t("close"), on: { click: close } }),
    ),
    bidiText(card.front, "card-front"),
    card.ipa ? h("div", { class: "card-ipa muted", text: card.ipa, dir: "ltr" }) : null,
    h("button", {
      class: "ghost audio",
      text: "🔊",
      title: t("playAudio"),
      on: { click: () => void playCard(card.audio_url, card.front, "en-US") },
    }),
    h("div", { class: "card-back" },
      bidiText(card.back, "card-answer"),
      // Only labelled when there are two senses to tell apart. Labelling a single
      // meaning would add chrome without adding information.
      card.back_general ? h("div", { class: "sense" },
        h("span", { class: "sense-label muted small", text: t("senseGeneral") }),
        bidiText(card.back_general, "sense-text"),
      ) : null,
      card.example ? bidiText(card.example, "card-example muted") : null,
      card.example_back ? bidiText(card.example_back, "card-example muted") : null,
      card.notes ? bidiText(card.notes, "card-example muted") : null,
    ),
    noteEditor(card),
  );

  const overlay = h("div", { class: "preview-overlay", on: { click: close } }, panel);

  document.body.append(overlay);
  addEventListener("keydown", onKey);
  panel.querySelector<HTMLButtonElement>("button")?.focus();
}

/**
 * The learner's private note on a card.
 *
 * Saves on blur rather than behind a button: the note is incidental to the task,
 * and a Save button someone forgets to press loses their work silently.
 */
function noteEditor(card: Card): HTMLElement {
  const status = h("span", { class: "muted small" });
  const box = h("textarea", {
    class: "note-box",
    placeholder: t("notePlaceholder"),
    attrs: { rows: "2" },
  }) as HTMLTextAreaElement;

  let loaded = "";
  void loadCardNote(card.id)
    .then((note) => {
      loaded = note;
      box.value = note;
    })
    .catch(() => {
      // Signed out, or the table is missing because migration-002 has not run.
      // Neither should break browsing, so just hide the editor.
      wrap.hidden = true;
    });

  box.addEventListener("blur", () => {
    if (box.value === loaded) return; // nothing changed; don't write
    void saveCardNote(card.id, box.value)
      .then(() => {
        loaded = box.value;
        status.textContent = t("noteSaved");
        setTimeout(() => (status.textContent = ""), 2000);
      })
      .catch((error: unknown) => {
        status.textContent = error instanceof Error ? error.message : t("errGeneric");
      });
  });

  const wrap = h("div", { class: "note-wrap" },
    h("div", { class: "note-head" },
      h("span", { class: "muted small", text: t("myNote") }),
      status,
    ),
    box,
  );
  return wrap;
}

export function spinner(): HTMLElement {
  return h("p", { class: "muted", text: t("loading") });
}

/**
 * A card text field: a textarea that grows to fit instead of a single-line input.
 *
 * Card content is often a phrase rather than a word — "to take something for
 * granted", or a Persian gloss with a qualifier — and in a one-line input on a
 * phone that scrolls sideways, hiding the start of what you just typed. Growing
 * downward keeps the whole value visible while you check it.
 *
 * `dir` is left to the caller: the front is English by definition, while the back
 * is usually Persian but may contain English, so it gets `auto` plus the
 * plaintext bidi treatment in CSS — that resolves direction per line rather than
 * once for the whole box, which is what multi-line mixed content needs.
 */
export function growingField(
  placeholder: string,
  opts: { dir?: "ltr" | "auto"; value?: string } = {},
): HTMLTextAreaElement {
  const box = h("textarea", {
    class: "card-field",
    placeholder,
    dir: opts.dir,
    value: opts.value,
    attrs: { rows: "1" },
  }) as HTMLTextAreaElement;

  const fit = () => {
    box.style.blockSize = "auto";
    // box-sizing is border-box globally, so the height has to include the
    // borders that scrollHeight leaves out — otherwise every box is 2px short
    // and scrolls by exactly that.
    const borders = box.offsetHeight - box.clientHeight;
    box.style.blockSize = `${box.scrollHeight + borders}px`;
  };

  box.addEventListener("input", fit);
  // Only a pre-filled box needs fitting before the first keystroke, and only
  // then is the deferral worth it: scrollHeight reads as 0 until the element is
  // in the document. An empty box is already the right size via min-block-size,
  // so the common case doesn't depend on a frame being painted at all.
  if (opts.value) requestAnimationFrame(fit);
  return box;
}
